import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { messageForError } from '@proctoring/api-client';
import { useApi } from '../../auth/AuthProvider.js';

type CheckState = 'idle' | 'running' | 'passed' | 'failed';

interface CheckResult {
  state: CheckState;
  detail: string;
}

const IDLE: CheckResult = { state: 'idle', detail: 'Not run yet.' };

const STATE_CLASSES: Record<CheckState, string> = {
  idle: 'border-session-border bg-session-panel-raised text-session-dim',
  running: 'border-severity-info/40 bg-severity-info/10 text-severity-info',
  passed: 'border-severity-info/40 bg-severity-info/10 text-severity-info',
  failed: 'border-severity-critical/40 bg-severity-critical/10 text-severity-critical',
};

const STATE_LABELS: Record<CheckState, string> = {
  idle: 'Not run',
  running: 'Checking…',
  passed: 'Pass',
  failed: 'Fail',
};

function CheckRow({
  title,
  description,
  result,
}: {
  title: string;
  description: string;
  result: CheckResult;
}): ReactElement {
  return (
    <div
      data-testid={`check-${title.toLowerCase().replace(/\s+/g, '-')}`}
      data-state={result.state}
      className="flex items-start justify-between gap-4 border-b border-session-border px-5 py-4 last:border-b-0"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-session-text">{title}</p>
        <p className="mt-0.5 text-sm text-session-muted">{description}</p>
        <p className="mt-1 text-xs text-session-dim">{result.detail}</p>
      </div>
      <span
        className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATE_CLASSES[result.state]}`}
      >
        {STATE_LABELS[result.state]}
      </span>
    </div>
  );
}

/**
 * The browser-context system check.
 *
 * Deliberately a *subset* of the real preflight (design §4.1): a web page cannot enumerate
 * displays, scan for blacklisted processes, or detect a VM. It verifies only what the
 * browser exposes — camera, microphone and API reachability — and says so plainly, so a
 * candidate never reads a green page here as clearance to skip the desktop check.
 */
export function SystemCheckPage(): ReactElement {
  const api = useApi();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [camera, setCamera] = useState<CheckResult>(IDLE);
  const [microphone, setMicrophone] = useState<CheckResult>(IDLE);
  const [network, setNetwork] = useState<CheckResult>(IDLE);

  /*
   * Which media check the stream in flight belongs to.
   *
   * `getUserMedia` resolves *after* an unmount or a second click, and by then `stopPreview`
   * has already run against a ref that was still null — so the arriving stream would be
   * stored with nothing left to stop it, and the camera light stays on after the candidate
   * navigates away. Bumping this on every run and on unmount lets the resolver notice it is
   * stale and stop the tracks itself.
   */
  const runIdRef = useRef(0);

  const stopPreview = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // Releasing the devices on unmount matters: a camera light left on after the candidate
  // navigates away reads as the app spying on them.
  useEffect(
    () => () => {
      runIdRef.current += 1;
      stopPreview();
    },
    [stopPreview],
  );

  const runMediaCheck = useCallback(async () => {
    // Claims this run. Anything already in flight becomes stale and releases its own tracks.
    runIdRef.current += 1;
    const runId = runIdRef.current;

    setCamera({ state: 'running', detail: 'Waiting for permission…' });
    setMicrophone({ state: 'running', detail: 'Waiting for permission…' });
    stopPreview();

    const devices = globalThis.navigator?.mediaDevices;
    if (!devices?.getUserMedia) {
      const detail = 'This browser does not expose camera and microphone access.';
      setCamera({ state: 'failed', detail });
      setMicrophone({ state: 'failed', detail });
      return;
    }

    try {
      const stream = await devices.getUserMedia({ video: true, audio: true });
      // Stale by the time permission was granted — the candidate left the page, or started
      // another check. Stop what was just opened rather than storing a stream nothing owns.
      if (runId !== runIdRef.current) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Autoplay can still be blocked; the preview simply stays black rather than throwing.
        void videoRef.current.play().catch(() => {});
      }

      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];

      setCamera(
        videoTrack
          ? { state: 'passed', detail: `Using “${videoTrack.label || 'default camera'}”.` }
          : { state: 'failed', detail: 'Permission granted but no camera was found.' },
      );
      setMicrophone(
        audioTrack
          ? { state: 'passed', detail: `Using “${audioTrack.label || 'default microphone'}”.` }
          : { state: 'failed', detail: 'Permission granted but no microphone was found.' },
      );
    } catch (cause) {
      const denied = cause instanceof DOMException && cause.name === 'NotAllowedError';
      const detail = denied
        ? 'Permission was denied. Allow camera and microphone access, then run the check again.'
        : messageForError(cause);
      setCamera({ state: 'failed', detail });
      setMicrophone({ state: 'failed', detail });
    }
  }, [stopPreview]);

  const runNetworkCheck = useCallback(async () => {
    setNetwork({ state: 'running', detail: 'Contacting the interview server…' });
    const startedAt = performance.now();
    try {
      await api.health();
      const ms = Math.round(performance.now() - startedAt);
      setNetwork({ state: 'passed', detail: `Interview server reachable in ${ms} ms.` });
    } catch (cause) {
      setNetwork({ state: 'failed', detail: messageForError(cause) });
    }
  }, [api]);

  const [running, setRunning] = useState(false);

  const runAll = useCallback(async () => {
    setRunning(true);
    try {
      await Promise.all([runMediaCheck(), runNetworkCheck()]);
    } finally {
      setRunning(false);
    }
  }, [runMediaCheck, runNetworkCheck]);

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-10">
      <h1 className="text-xl font-semibold tracking-tight text-session-text">System check</h1>
      <p className="mt-1 text-sm text-session-muted">
        A quick browser-side check of the basics. Run it as early as you like, and again on the day.
      </p>

      <p
        role="note"
        className="mt-5 rounded-md border border-severity-warning/40 bg-severity-warning/10 px-4 py-3 text-sm text-severity-warning"
      >
        This page only checks what a browser can see. The <strong>full machine check</strong> —
        single display, screen-recording permission, no remote-desktop or overlay software, no
        virtual machine — runs inside the desktop app, and the interview cannot start until it
        passes there.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void runAll()}
          disabled={running}
          className="btn-primary"
        >
          {running ? 'Running…' : 'Run all checks'}
        </button>
        <button type="button" onClick={stopPreview} className="btn-secondary">
          Stop camera preview
        </button>
      </div>

      <div className="panel mt-6 overflow-hidden">
        <CheckRow
          title="Camera"
          description="Your webcam must be available and permitted."
          result={camera}
        />
        <CheckRow
          title="Microphone"
          description="Your microphone must be available and permitted."
          result={microphone}
        />
        <CheckRow
          title="Network"
          description="Your network must be able to reach the interview server."
          result={network}
        />
      </div>

      <section className="panel mt-6 p-5" aria-labelledby="preview-heading">
        <h2 id="preview-heading" className="text-sm font-semibold text-session-text">
          Camera preview
        </h2>
        <p className="mt-1 mb-4 text-sm text-session-muted">
          Check your framing and lighting. Your face should be clearly visible.
        </p>
        <div className="aspect-video w-full max-w-md overflow-hidden rounded-md border border-session-border bg-black">
          <video
            ref={videoRef}
            data-testid="camera-preview"
            muted
            playsInline
            className="h-full w-full object-cover"
            aria-label="Live camera preview"
          />
        </div>
      </section>
    </div>
  );
}
